import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { fmtDate } from "@/lib/locale";

interface InlineSoldDateCellProps {
  itemId: string;
  value: string | null;
  onSaved: () => void;
  queryKeys: string[][];
  testIdPrefix: string;
  justify?: boolean;
}

export function InlineSoldDateCell({
  itemId,
  value,
  onSaved,
  queryKeys,
  testIdPrefix,
  justify = false,
}: InlineSoldDateCellProps) {
  const { toast } = useToast();
  const { t } = useTranslation(["inventory", "common"]);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const saveMutation = useMutation({
    mutationFn: (date: string) =>
      apiRequest("PATCH", `/api/inventory/${itemId}/sold-details`, { saleDate: date }),
    onSuccess: () => {
      for (const key of queryKeys) {
        qc.invalidateQueries({ queryKey: key });
      }
      setEditing(false);
      onSaved();
    },
    onError: (err: any) => {
      toast({ title: t("inventory:toast.error"), description: err.message, variant: "destructive" });
    },
  });

  function startEdit() {
    setInputVal(value ?? new Date().toISOString().slice(0, 10));
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function confirm() {
    if (!inputVal) { setEditing(false); return; }
    saveMutation.mutate(inputVal);
  }

  function cancelEdit() {
    setEditing(false);
  }

  if (editing) {
    return (
      <div
        className={`flex items-center gap-1${justify ? " justify-end" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {saveMutation.isPending && (
          <span className="text-[10px] text-muted-foreground animate-pulse">{t("inventory:actions.savingInline")}</span>
        )}
        <input
          ref={inputRef}
          type="date"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onBlur={confirm}
          onKeyDown={(e) => {
            if (e.key === "Enter") confirm();
            if (e.key === "Escape") cancelEdit();
          }}
          className="text-xs border border-primary/40 rounded px-1.5 py-0.5 bg-background outline-none focus:ring-1 focus:ring-primary"
          data-testid={`input-${testIdPrefix}-saleDate-${itemId}`}
          autoFocus
        />
      </div>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); startEdit(); }}
      className="font-medium text-xs hover:underline cursor-pointer hover:text-primary transition-colors"
      title={t("inventory:inlineSoldDate.editTitle")}
      data-testid={`cell-${testIdPrefix}-saleDate-${itemId}`}
    >
      {value ? fmtDate(value) : <span className="text-muted-foreground">—</span>}
    </button>
  );
}
